import { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useCreateKnowledgeItem } from "@/hooks/knowledge/useKnowledgeItems";
import { FileUp, AlertCircle, RefreshCw, Building, User, Mic } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import fileService, { KnowledgeItem } from "@/services/fileService";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { checkPlanLimits } from "@/lib/planLimits";
import { useCombinedAgentList } from "@/hooks/agent/useCombinedAgentList";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

// Define accepted file types
const ACCEPTED_FILE_TYPES = {
  "application/pdf": [".pdf"],
  "text/plain": [".txt"],
  "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
} as const;

const ACCEPTED_MIME_TYPES = Object.keys(ACCEPTED_FILE_TYPES);
const ACCEPTED_EXTENSIONS = Object.values(ACCEPTED_FILE_TYPES).flat();

const fileSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  file: z
    .any()
    .refine((file) => file instanceof File, "Please select a file")
    .refine(
      (file) => file.size <= MAX_FILE_SIZE,
      "File size must be less than 20MB"
    )
    .refine(
      (file) => {
        // Check both MIME type and file extension
        const fileExtension = file.name.toLowerCase().match(/\.[^.]+$/)?.[0];
        return ACCEPTED_MIME_TYPES.includes(file.type) || 
               (fileExtension && ACCEPTED_EXTENSIONS.some(ext => ext === fileExtension));
      },
      `Only ${ACCEPTED_EXTENSIONS.join(", ")} files are accepted`
    ),
});

interface AddFileFormProps {
  onSuccess: (item?: KnowledgeItem) => void;
  agentId?: string | null;
}

const AddFileForm = ({
  onSuccess,
  agentId: initialAgentId,
}: AddFileFormProps) => {
  const { organization } = useAuth();
  const { createItem, isLoading: isCreatingItem } = useCreateKnowledgeItem();
  const { agents, isLoading: isAgentsLoading } = useCombinedAgentList();
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [fileName, setFileName] = useState("");
  const [aiProcessingFailed, setAiProcessingFailed] = useState(false);
  const [savedItemData, setSavedItemData] = useState<{
    id: string;
    filePath: string;
    file: File;
    title: string;
    description: string | null;
  } | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  // Agent selection state
  const [isAgentSpecific, setIsAgentSpecific] = useState(!!initialAgentId);
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(
    initialAgentId || null
  );

  // Get the organization ID
  const orgId = organization?.id;

  const form = useForm<z.infer<typeof fileSchema>>({
    resolver: zodResolver(fileSchema),
    defaultValues: {
      title: "",
      description: "",
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Clear any previous validation errors
    form.clearErrors("file");

    // Validate file type
    const fileExtension = file.name.toLowerCase().match(/\.[^.]+$/)?.[0];
    const isValidType = ACCEPTED_MIME_TYPES.includes(file.type) || 
                       (fileExtension && ACCEPTED_EXTENSIONS.some(ext => ext === fileExtension));
    
    if (!isValidType) {
      form.setError("file", {
        type: "validate",
        message: `Only ${ACCEPTED_EXTENSIONS.join(", ")} files are accepted`,
      });
      e.target.value = "";
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      form.setError("file", {
        type: "validate",
        message: "File size must be less than 20MB",
      });
      e.target.value = "";
      return;
    }

    // Reset AI processing error state when a new file is selected
    setAiProcessingFailed(false);
    setSavedItemData(null);

    form.setValue("file", file);
    setFileName(file.name);

    // Auto-fill title with file name (without extension) if title is empty
    if (!form.getValues("title")) {
      const nameWithoutExtension = file.name.split(".").slice(0, -1).join(".");
      form.setValue("title", nameWithoutExtension || file.name);
    }
  };

  const handleAgentSpecificToggle = (checked: boolean) => {
    setIsAgentSpecific(checked);
    if (!checked) {
      setSelectedAgentId(null);
    }
  };

  const handleRetry = async () => {
    if (!savedItemData || !orgId) return;

    setIsRetrying(true);
    try {
      const result = await fileService.processKnowledgeItem(savedItemData.id, orgId);
      
      if (result.success) {
        setAiProcessingFailed(false);
        setSavedItemData(null);
        toast({
          title: "AI processing successful",
          description: "Your file is now searchable by AI agents.",
        });
      } else {
        throw new Error(result.error || "Failed to process file for AI");
      }
    } catch (error) {
      console.error("Error retrying file processing:", error);
      toast({
        variant: "destructive",
        title: "Retry failed",
        description: error instanceof Error ? error.message : "Failed to process file for AI",
      });
    } finally {
      setIsRetrying(false);
    }
  };

  const onSubmit = async (values: z.infer<typeof fileSchema>) => {
    if (!orgId) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "No organization found",
      });
      return;
    }

    // Validate agent selection if agent-specific is enabled
    if (isAgentSpecific && !selectedAgentId) {
      toast({
        variant: "destructive",
        title: "Agent required",
        description:
          "Please select an agent or switch to organization-level upload",
      });
      return;
    }

    setIsUploading(true);
    setAiProcessingFailed(false);
    setSavedItemData(null);

    try {
      let limitError = await checkPlanLimits(orgId, "knowledge_items");
      if (limitError) {
        toast({
          variant: "destructive",
          title: "Plan Limit Reached",
          description: limitError.message,
        });
        setIsUploading(false);
        return;
      }

      const file = values.file as File;
      const fileExt = file.name.split(".").pop();
      const safeName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, "_");
      const filePath = `${orgId}/${crypto.randomUUID()}_${safeName}`;

      // Calculate size once
      const fileSizeMB = parseFloat((file.size / 1024 / 1024).toFixed(2));

      // Check storage limits before uploading
      limitError = await checkPlanLimits(orgId, "storage", fileSizeMB);
      if (limitError) {
        toast({
          variant: "destructive",
          title: "Plan Limit Reached",
          description: limitError.message,
        });
        setIsUploading(false);
        return;
      }

      // Proceed with the upload now that we know it fits the plan
      const { error: uploadError } = await supabase.storage
        .from("knowledge_files")
        .upload(filePath, file, { upsert: false });

      if (uploadError) {
        throw uploadError;
      }

      // Increment files_count and storage_usage_mb for the organization
      if (orgId) {
        try {
          const { data: orgData, error: orgError } = await supabase
            .from("organizations")
            .select("id")
            .eq("id", orgId)
            .single();
          if (!orgError && orgData) {
            await supabase.rpc("increment_org_usage", {
              org_id: orgId,
              inc_files: 0,
              inc_storage: fileSizeMB,
            });
            console.log(
              "[KNOWLEDGE] Incremented files_count by 1 and storage_usage_mb by",
              fileSizeMB,
              "for organization:",
              orgId
            );
          }
        } catch (err) {
          console.error(
            "Failed to increment files_count or storage_usage_mb:",
            err
          );
        }
      }

      // Create knowledge item record
      const finalAgentId = isAgentSpecific ? selectedAgentId : null;
      const selectedAgent = finalAgentId ? agents.find(a => a.id === finalAgentId) : null;
      const isVoiceAgent = selectedAgent?.type === "voice";

      // Create knowledge item record with agent_id or voice_agent_id if specified
      const result = await createItem({
        title: values.title || file.name,
        description: values.description || null,
        type: "file",
        file_path: filePath,
        file_name: file.name,
        file_type: fileExt || file.type,
        file_size: file.size,
        ...(isVoiceAgent 
          ? { voice_agent_id: finalAgentId }
          : { agent_id: finalAgentId }
        ),
      });

      if (result) {
        // Create KnowledgeItem object to pass back to parent
        const item: KnowledgeItem = {
          id: result.id,
          title: values.title || file.name,
          type: "file",
          file_path: filePath,
          file_name: file.name,
          file_type: fileExt || file.type,
          file_size: file.size,
          organization_id: orgId,
          ai_processed: false,
          agent_id: finalAgentId,
        };

        // Save item data for potential retry
        setSavedItemData({
          id: result.id,
          filePath,
          file,
          title: values.title || file.name,
          description: values.description || null,
        });

        // Process the file for AI using the unified service
        try {
          const processResult = await fileService.processKnowledgeItem(result.id, orgId);
          
          if (processResult.success) {
            item.ai_processed = true;
            toast({
              title: "File uploaded successfully",
              description: "Your file is now searchable by AI agents.",
            });
          } else {
            // File was saved but AI processing failed
            setAiProcessingFailed(true);
            console.error("AI processing failed:", processResult.error);
            toast({
              variant: "destructive",
              title: "File uploaded, AI processing failed",
              description: "The file was saved but could not be processed for AI search. You can retry processing.",
            });
          }
        } catch (error) {
          // File was saved but AI processing failed
          setAiProcessingFailed(true);
          console.error("Error processing file for AI:", error);
          toast({
            variant: "destructive",
            title: "File uploaded, AI processing failed",
            description: "The file was saved but could not be processed for AI search. You can retry processing.",
          });
        }

        onSuccess(item);
        form.reset();
        setFileName("");
        setIsAgentSpecific(!!initialAgentId);
        setSelectedAgentId(initialAgentId || null);
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      toast({
        variant: "destructive",
        title: "Upload failed",
        description:
          error instanceof Error ? error.message : "Failed to upload file",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const getSelectedAgentName = () => {
    if (!selectedAgentId) return null;
    const agent = agents.find((a) => a.id === selectedAgentId);
    return agent ? agent.title : "Unknown Agent";
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {savedItemData && aiProcessingFailed && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>AI Processing Failed</AlertTitle>
            <AlertDescription>
              The file was uploaded but could not be processed for AI search.
              <Button
                variant="outline"
                size="sm"
                onClick={handleRetry}
                disabled={isRetrying}
                className="mt-2 w-full"
              >
                {isRetrying ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Retrying...
                  </>
                ) : (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Retry AI Processing
                  </>
                )}
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Agent Selection Section */}
        <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="agent-specific" className="text-sm font-medium">
                Upload for specific agent
              </Label>
              <p className="text-xs text-muted-foreground">
                Choose whether this file should be available to all agents or
                just one specific agent
              </p>
            </div>
            <Switch
              id="agent-specific"
              checked={isAgentSpecific}
              onCheckedChange={handleAgentSpecificToggle}
              disabled={isAgentsLoading}
            />
          </div>

          {isAgentSpecific ? (
            <div className="space-y-2">
              <Label className="text-sm">Select Agent</Label>
              <Select
                value={selectedAgentId || ""}
                onValueChange={setSelectedAgentId}
                disabled={isAgentsLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose an agent..." />
                </SelectTrigger>
                <SelectContent>
                  {agents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      <div className="flex items-center">
                        {agent.type === "voice" ? (
                          <Mic className="mr-2 h-4 w-4 text-blue-600" />
                        ) : (
                        <User className="mr-2 h-4 w-4" />
                        )}
                        {agent.title}
                        {agent.type === "voice" && (
                          <span className="ml-2 text-xs text-muted-foreground">(Voice)</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedAgentId && (
                <Alert className="mt-2">
                  <User className="h-4 w-4" />
                  <AlertTitle>Agent-Specific File</AlertTitle>
                  <AlertDescription>
                    This file will be uploaded for{" "}
                    <strong>{getSelectedAgentName()}</strong> and will only be
                    accessible to that agent.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          ) : (
            <Alert>
              <Building className="h-4 w-4" />
              <AlertTitle>Organization File</AlertTitle>
              <AlertDescription>
                This file will be uploaded to the organization and will be
                accessible to all agents.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <FormField
          control={form.control}
          name="file"
          render={({ field: { onChange, value, ...rest } }) => (
            <FormItem>
              <FormLabel>File ({ACCEPTED_EXTENSIONS.join(", ")}, max 20MB)</FormLabel>
              <FormControl>
                <div className="grid w-full gap-2">
                  <Input
                    type="file"
                    accept={[...ACCEPTED_MIME_TYPES, ...ACCEPTED_EXTENSIONS].join(",")}
                    id="knowledge-file-upload"
                    className="hidden"
                    onChange={handleFileChange}
                    {...rest}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() =>
                      document.getElementById("knowledge-file-upload")?.click()
                    }
                  >
                    <FileUp className="mr-2 h-4 w-4" />
                    {fileName || "Select File"}
                  </Button>
                  {fileName && (
                    <p className="text-sm text-muted-foreground truncate">
                      Selected: {fileName}
                    </p>
                  )}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title (Optional)</FormLabel>
              <FormControl>
                <Input placeholder="Enter a title for this file" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description (Optional)</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Add a brief description of this file"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          disabled={
            isUploading ||
            isCreatingItem ||
            (isAgentSpecific && !selectedAgentId)
          }
          className="w-full"
        >
          {isUploading ? (
            <>
              <span className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
              Uploading...
            </>
          ) : (
            <>
              <FileUp className="mr-2 h-4 w-4" />
              Upload File{" "}
              {isAgentSpecific && selectedAgentId
                ? `to ${getSelectedAgentName()}`
                : "to Organization"}
            </>
          )}
        </Button>
      </form>
    </Form>
  );
};

export default AddFileForm;
